import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import API_ENDPOINTS from 'utils/api/endpoints';
import { formatCurrency } from 'utils/helper';
import { Icon } from 'utils/icons';

const MAX_MESSAGE_LENGTH = 1500;

const STARTER_PROMPTS = [
  'How many total cast and crew are in this budget?',
  'Are any areas overspending compared with the rest of the budget?',
  'Build a category for a movie premiere.',
];

const actionSummary = action => {
  const rowCount = action?.rows?.length || 0;
  if (action?.action === 'create_category') {
    return `${action.category_name || 'New category'}${
      rowCount ? ` · ${rowCount} item${rowCount === 1 ? '' : 's'}` : ''
    }`;
  }
  return `${rowCount} new item${rowCount === 1 ? '' : 's'} in ${
    action?.category_name || 'the selected detail view'
  }`;
};

const legacyParagraphs = value => {
  const content = String(value || '').trim();
  if (!content) return [];
  const explicitParagraphs = content
    .split(/\n\s*\n/)
    .map(part => part.trim())
    .filter(Boolean);
  if (explicitParagraphs.length > 1 || content.length <= 220) {
    return explicitParagraphs;
  }
  return (
    content.match(/[^.!?]+(?:[.!?]+(?=\s|$)|$)/g)?.map(part => part.trim()) ||
    explicitParagraphs
  ).filter(Boolean);
};

const InlineMarkdown = ({ children }) => {
  const text = String(children || '');
  const tokens = text.split(
    /(\*\*[^*\n]+\*\*|__[^_\n]+__|`[^`\n]+`|\*[^*\n]+\*|_[^_\n]+_)/g
  );

  return (
    <>
      {tokens.map((token, index) => {
        const key = `${token}-${index}`;
        if (
          (token.startsWith('**') && token.endsWith('**')) ||
          (token.startsWith('__') && token.endsWith('__'))
        ) {
          return <strong key={key}>{token.slice(2, -2)}</strong>;
        }
        if (
          (token.startsWith('*') && token.endsWith('*')) ||
          (token.startsWith('_') && token.endsWith('_'))
        ) {
          return <em key={key}>{token.slice(1, -1)}</em>;
        }
        if (token.startsWith('`') && token.endsWith('`')) {
          return <code key={key}>{token.slice(1, -1)}</code>;
        }
        return <React.Fragment key={key}>{token}</React.Fragment>;
      })}
    </>
  );
};

InlineMarkdown.propTypes = {
  children: PropTypes.string,
};

InlineMarkdown.defaultProps = {
  children: '',
};

const LabeledText = ({ children }) => {
  const text = String(children || '');
  const labelMatch = /^(?:\*\*|__)/.test(text)
    ? null
    : text.match(/^([^:\n]{1,80}:)\s*(.*)$/s);
  return (
    <>
      {labelMatch ? (
        <>
          <strong>
            <InlineMarkdown>{labelMatch[1]}</InlineMarkdown>
          </strong>
          {labelMatch[2] && (
            <>
              {' '}
              <InlineMarkdown>{labelMatch[2]}</InlineMarkdown>
            </>
          )}
        </>
      ) : (
        <InlineMarkdown>{text}</InlineMarkdown>
      )}
    </>
  );
};

LabeledText.propTypes = {
  children: PropTypes.string,
};

LabeledText.defaultProps = {
  children: '',
};

const LabeledParagraph = ({ children }) => (
  <p>
    <LabeledText>{children}</LabeledText>
  </p>
);

LabeledParagraph.propTypes = LabeledText.propTypes;
LabeledParagraph.defaultProps = LabeledText.defaultProps;

const MarkdownContent = ({ children }) => {
  const groups = [];
  let currentList = null;

  const flushList = () => {
    if (currentList) groups.push(currentList);
    currentList = null;
  };

  String(children || '')
    .split('\n')
    .forEach(rawLine => {
      const line = rawLine.trim();
      const unordered = line.match(/^(?:[-+•]|\*(?!\*))\s+(.+)$/);
      const ordered = line.match(/^\d+[.)]\s+(.+)$/);
      const heading = line.match(/^#{1,6}\s+(.+)$/);

      if (unordered || ordered) {
        const type = unordered ? 'ul' : 'ol';
        if (!currentList || currentList.type !== type) {
          flushList();
          currentList = { type, items: [] };
        }
        currentList.items.push((unordered || ordered)[1]);
        return;
      }

      flushList();
      if (!line) return;
      groups.push({
        type: heading ? 'heading' : 'paragraph',
        text: heading ? heading[1] : line,
      });
    });
  flushList();

  return groups.map((group, index) => {
    const key = `${group.type}-${index}`;
    if (group.type === 'ul' || group.type === 'ol') {
      const List = group.type;
      return (
        <List key={key}>
          {group.items.map((item, itemIndex) => (
            <li key={`${key}-${itemIndex}`}>
              <LabeledText>{item}</LabeledText>
            </li>
          ))}
        </List>
      );
    }
    if (group.type === 'heading') {
      return (
        <strong className="ai-assistant__response-heading" key={key}>
          <InlineMarkdown>{group.text}</InlineMarkdown>
        </strong>
      );
    }
    return <LabeledParagraph key={key}>{group.text}</LabeledParagraph>;
  });
};

MarkdownContent.propTypes = LabeledText.propTypes;
MarkdownContent.defaultProps = LabeledText.defaultProps;

const cleanMarkdownHeading = value => {
  let heading = String(value || '')
    .replace(/^#{1,6}\s+/, '')
    .trim();
  if (
    (heading.startsWith('**') && heading.endsWith('**')) ||
    (heading.startsWith('__') && heading.endsWith('__'))
  ) {
    heading = heading.slice(2, -2);
  }
  return heading;
};

const AssistantReply = ({ message }) => {
  const blocks = Array.isArray(message?.reply_blocks)
    ? message.reply_blocks
    : [];
  return (
    <div className="ai-assistant__formatted">
      {blocks.length
        ? blocks.map((block, index) => (
            <section
              className="ai-assistant__response-block"
              key={`${message.id}-block-${index}`}
            >
              {block.heading && (
                <strong className="ai-assistant__response-heading">
                  <InlineMarkdown>
                    {cleanMarkdownHeading(block.heading)}
                  </InlineMarkdown>
                </strong>
              )}
              {block.text && <MarkdownContent>{block.text}</MarkdownContent>}
              {block.type === 'bullets' && block.items?.length > 0 && (
                <ul>
                  {block.items.map((item, itemIndex) => (
                    <li key={`${message.id}-block-${index}-${itemIndex}`}>
                      <LabeledText>{item}</LabeledText>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))
        : legacyParagraphs(message?.content).map((paragraph, index) => (
            <MarkdownContent key={`${message.id}-paragraph-${index}`}>
              {paragraph}
            </MarkdownContent>
          ))}
    </div>
  );
};

AssistantReply.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    content: PropTypes.string,
    reply_blocks: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(['paragraph', 'bullets']),
        heading: PropTypes.string,
        text: PropTypes.string,
        items: PropTypes.arrayOf(PropTypes.string),
      })
    ),
  }).isRequired,
};

const AppliedReceipt = ({ message }) => {
  const result = message?.apply_result || {};
  const additions = Array.isArray(result.additions) ? result.additions : [];

  return (
    <div className="ai-assistant__receipt">
      <strong className="ai-assistant__applied">Added</strong>
      {additions.length ? (
        additions.map(addition => (
          <dl
            className="ai-assistant__receipt-entry"
            key={addition.category_id || addition.category_name}
          >
            <div>
              <dt>Category</dt>
              <dd>{addition.category_name || 'New category'}</dd>
            </div>
            <div>
              <dt>Location</dt>
              <dd>{addition.location || 'Top level'}</dd>
            </div>
            <div>
              <dt>Total added</dt>
              <dd>
                {formatCurrency(
                  addition.total_added || 0,
                  addition.currency || result.currency
                )}
              </dd>
            </div>
          </dl>
        ))
      ) : (
        <span className="ai-assistant__receipt-fallback">Added to budget</span>
      )}
    </div>
  );
};

AppliedReceipt.propTypes = {
  message: PropTypes.shape({
    apply_result: PropTypes.shape({
      currency: PropTypes.string,
      additions: PropTypes.arrayOf(
        PropTypes.shape({
          category_id: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
          ]),
          category_name: PropTypes.string,
          location: PropTypes.string,
          total_added: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
          ]),
          currency: PropTypes.string,
        })
      ),
    }),
  }).isRequired,
};

const AiAssistantTab = ({ budgetId, categoryId, onBudgetChange }) => {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [applyingId, setApplyingId] = useState(null);
  const [error, setError] = useState('');
  const [canEdit, setCanEdit] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!budgetId) return undefined;
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    API_ENDPOINTS.ai_suggestions
      .assistantList(budgetId, { signal: controller.signal })
      .then(response => {
        if (!active) return;
        setMessages(response?.data?.messages || []);
        setCanEdit(Boolean(response?.data?.can_edit));
      })
      .catch(requestError => {
        if (!active || requestError?.code === 'ERR_CANCELED') return;
        setError(
          requestError?.response?.data?.detail ||
            'The AI Assistant is unavailable right now.'
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [budgetId]);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    if (typeof element.scrollTo === 'function') {
      element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
    } else {
      element.scrollTop = element.scrollHeight;
    }
  }, [messages, sending]);

  const sendMessage = async value => {
    const message = String(value ?? draft).trim();
    if (!message || sending || message.length > MAX_MESSAGE_LENGTH) return;
    const optimisticMessageId = `pending-user-${Date.now()}`;
    const optimisticMessage = {
      id: optimisticMessageId,
      role: 'user',
      content: message,
      actions: [],
    };

    setSending(true);
    setError('');
    setDraft('');
    setMessages(previous => [...previous, optimisticMessage]);
    try {
      const response = await API_ENDPOINTS.ai_suggestions.assistantAsk({
        budget_id: budgetId,
        category_id: categoryId || null,
        message,
      });
      setMessages(previous => [
        ...previous.filter(item => item.id !== optimisticMessageId),
        response.data.user_message,
        response.data.assistant_message,
      ]);
      setCanEdit(Boolean(response?.data?.can_edit));
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          requestError?.response?.data?.detail ||
          'The AI Assistant is unavailable right now.'
      );
    } finally {
      setSending(false);
    }
  };

  const applyProposal = async message => {
    if (!message?.id || applyingId) return;
    setApplyingId(message.id);
    setError('');
    try {
      const response = await API_ENDPOINTS.ai_suggestions.assistantApply({
        budget_id: budgetId,
        message_id: message.id,
      });
      setMessages(previous =>
        previous.map(item =>
          item.id === message.id
            ? {
                ...response.data.message,
                apply_result:
                  response.data.message?.apply_result || response.data.result,
              }
            : item
        )
      );
      await onBudgetChange?.(response.data.result);
      const createdCount =
        (response.data.result?.created_categories?.length || 0) +
        (response.data.result?.created_rows?.length || 0);
      toast.success(
        createdCount
          ? `Added ${createdCount} budget ${
              createdCount === 1 ? 'entry' : 'entries'
            }.`
          : 'No new budget entries were needed.'
      );
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail ||
          'Unable to add these items to the budget.'
      );
    } finally {
      setApplyingId(null);
    }
  };

  const handleKeyDown = event => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    event.currentTarget.form?.requestSubmit();
  };

  return (
    <div className="ai-assistant" role="tabpanel">
      <div className="ai-assistant__messages" ref={scrollRef}>
        {loading && (
          <p className="ai-insights__status">Loading your conversation…</p>
        )}
        {!loading && !messages.length && (
          <div className="ai-assistant__empty">
            <span className="ai-assistant__spark" aria-hidden="true">
              ✦
            </span>
            <h3>Ask about this budget</h3>
            <p>
              I can analyze totals and staffing, compare spending, or propose
              new categories and items.
            </p>
            <div className="ai-assistant__starters">
              {STARTER_PROMPTS.map(prompt => (
                <button
                  key={prompt}
                  type="button"
                  disabled={sending}
                  onClick={() => sendMessage(prompt)}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map(message => (
          <article
            className={`ai-assistant__message ai-assistant__message--${message.role}`}
            key={message.id}
          >
            <div className="ai-assistant__bubble">
              {message.role === 'assistant' ? (
                <AssistantReply message={message} />
              ) : (
                message.content
              )}
            </div>
            {message.role === 'assistant' && message.actions?.length > 0 && (
              <div className="ai-assistant__proposal">
                <strong>Proposed additions</strong>
                <ul>
                  {message.actions.map((action, index) => (
                    <li key={`${message.id}-${action.action}-${index}`}>
                      {actionSummary(action)}
                    </li>
                  ))}
                </ul>
                {message.applied_on ? (
                  <AppliedReceipt message={message} />
                ) : (
                  <button
                    type="button"
                    disabled={!canEdit || applyingId === message.id}
                    onClick={() => applyProposal(message)}
                  >
                    {applyingId === message.id ? 'Adding…' : 'Add to budget'}
                  </button>
                )}
                {!canEdit && (
                  <small>Edit access is required to add these items.</small>
                )}
              </div>
            )}
          </article>
        ))}
        {sending && (
          <div className="ai-assistant__thinking" aria-live="polite">
            <span />
            <span />
            <span />
          </div>
        )}
        {error && <p className="ai-insights__error">{error}</p>}
      </div>
      <form
        className="ai-assistant__composer"
        onSubmit={event => {
          event.preventDefault();
          sendMessage();
        }}
      >
        <textarea
          aria-label="Ask the AI Assistant about this budget"
          disabled={sending}
          maxLength={MAX_MESSAGE_LENGTH}
          onChange={event => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about this budget…"
          rows={3}
          value={draft}
        />
        <div className="ai-assistant__composer-footer">
          <span>
            {draft.length}/{MAX_MESSAGE_LENGTH}
          </span>
          <button
            aria-label="Send message to AI Assistant"
            disabled={!draft.trim() || sending}
            type="submit"
          >
            <Icon aria-hidden="true" name="ArrowUpIcon" />
          </button>
        </div>
      </form>
      <p className="ai-assistant__guardrail">
        AI can propose additions after your confirmation. It cannot edit or
        delete existing budget entries.
      </p>
    </div>
  );
};

AiAssistantTab.propTypes = {
  budgetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBudgetChange: PropTypes.func,
};

AiAssistantTab.defaultProps = {
  budgetId: null,
  categoryId: null,
  onBudgetChange: () => null,
};

export default AiAssistantTab;
